import { redactUrl } from './redact-url';

/**
 * Where a sign-in lands when no safe `?redirect=` says otherwise. ONE value
 * for every reader (PublicRoute, login, register): they disagreed (/app vs
 * /) until the phone-pass fix round.
 */
export const SIGNED_IN_HOME = '/app';

/**
 * The in-app path a `?redirect=` may send someone to after sign-in, or null.
 *
 * Only a RELATIVE path in this app is followed (phone pass P3-1): it must
 * start with `/`, resolve to this page's protocol and host, and not normalise
 * to `//host`. An absolute URL, a protocol-relative `//host`, a `javascript:`
 * / `data:` URL or a bare `relative/path` is refused, so a crafted login link
 * cannot bounce a fresh session off-site.
 *
 * Protocol + host, not `origin`: the iOS app runs at `capacitor://localhost`,
 * whose origin is the opaque "null", and comparing origins refused every
 * same-app path there (fix round A4). On such a non-special scheme the URL
 * parser does not turn `\` into `/`, so a backslash is refused outright.
 *
 * Returns the normalised path + query + hash, ready for navigate().
 */
export function safeRedirectPath(
  raw: string | null,
  base: string = window.location.href,
): string | null {
  if (!raw || !raw.startsWith('/') || raw.includes('\\')) return null;
  let here: URL;
  let url: URL;
  try {
    here = new URL(base);
    // The URL parser does what a browser would: on http(s) `/\host` and
    // `/\t/host` both become `//host`, which resolves to ANOTHER host and
    // is refused here.
    url = new URL(raw, here);
  } catch {
    return null;
  }
  if (url.protocol !== here.protocol || url.host !== here.host) return null;
  const path = `${url.pathname}${url.search}${url.hash}`;
  // Dot segments can normalise a same-app URL to a `//host` path
  // (`/.//evil`), which navigate() would hand to the browser as a
  // protocol-relative URL.
  return path.startsWith('//') ? null : path;
}

/** Where a sign-in goes: the safe `?redirect=`, else SIGNED_IN_HOME. */
export function signedInTarget(raw: string | null): string {
  return safeRedirectPath(raw) ?? SIGNED_IN_HOME;
}

/**
 * `path` carrying a safe `?redirect=` on to the next auth page (the
 * login ↔ register links), or bare `path` when there is none to carry.
 */
export function withRedirect(path: string, raw: string | null): string {
  const safe = safeRedirectPath(raw);
  return safe === null ? path : `${path}?redirect=${encodeURIComponent(safe)}`;
}

/**
 * Pages a sign-in never returns to: the auth pages themselves (a loop) and
 * the landing (a sign-in from there belongs at SIGNED_IN_HOME, as before).
 */
const NOT_A_DESTINATION = new Set([
  '/',
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
]);

/**
 * The login URL for a bounce whose sign-in should continue to `destination`:
 * `/login`, then `params`, then `?redirect=` when `destination` is a safe
 * in-app path (safeRedirectPath, so a bounce never carries an off-site URL)
 * worth coming back to and free of link secrets. ONE builder for every bounce (ProtectedRoute,
 * LandingRoute, the 401 interceptor): the interceptor built its own and
 * dropped the page (backlog c-079).
 */
export function loginUrl(destination: string | null, params: Record<string, string> = {}): string {
  const query = new URLSearchParams(params);
  const safe = safeRedirectPath(destination);
  if (safe !== null) {
    const path = safe.split(/[?#]/)[0];
    const bare = path.length > 1 ? path.replace(/\/+$/, '') : path;
    // A link secret (an RSVP or verification signature, a token) never rides
    // the login URL: encoded inside `redirect` it is invisible to redactUrl,
    // and the login URL is the page URL Sentry reports (backlog c-059).
    const carriesSecret = redactUrl(safe) !== safe;
    if (!NOT_A_DESTINATION.has(bare) && !carriesSecret) query.set('redirect', safe);
  }
  const qs = query.toString();
  return qs ? `/login?${qs}` : '/login';
}
